/**
 * Pure model + coercion for the dashboard preferences blob.
 *
 * Split out from the React provider (`./dashboardPrefs`) so the shape, the
 * reserved-surface constants, and the untrusted-input coercion are testable
 * without pulling React / the saved-views client into a unit test — and so both
 * the local cache and the server row are normalised through ONE function.
 */
import type { ReadScopeOverrides, WriteScopeOverrides } from "@authe/fe-commons/attribution";
import { TX_SCOPES, type TxScopeId } from "@/features/transactions/scopes";

/** Reserved saved-view surface that stores the dashboard prefs blob. */
export const PREFS_TABLE_ID = "__prefs:dashboard";
/** ScopedStorage key for the local cache of the whole blob (identity-namespaced). */
export const CACHE_KEY = "prefs:dashboard";
/** Stable name for the single prefs row (never shown; the surface is reserved). */
export const PREFS_VIEW_NAME = "dashboard-prefs";

/**
 * The two override maps, under the names the shared `ScopePrefsStore` uses.
 *
 * Aliases rather than declarations: this blob IS the dashboard's implementation
 * of that contract, and a locally-declared map shape is how a stored tier that
 * the shared resolver would reject gets written anyway.
 */
export type WriteScopeMap = WriteScopeOverrides;
export type ResolveScopeMap = ReadScopeOverrides;

/** The serialisable preferences blob (mirrors `view_state.extra` on the row). */
export interface DashboardPrefsBlob {
  writeScope: WriteScopeMap;
  resolveScope: ResolveScopeMap;
  txFeed: TxScopeId | null;
}

export const EMPTY_BLOB: DashboardPrefsBlob = { writeScope: {}, resolveScope: {}, txFeed: null };

/** True when `id` is a scope the transaction feed ladder still offers. */
export function isKnownTxScope(id: unknown): id is TxScopeId {
  return typeof id === "string" && TX_SCOPES.some((s) => s.id === id);
}

/**
 * Coerce an untrusted (local-cache / server) value into a well-formed blob. The
 * store never trusts either source's shape: non-object maps collapse to `{}`
 * and an unknown/absent tx-feed scope collapses to `null` (so a stale scope the
 * ladder dropped can't pin the explorer to a tier that no longer exists).
 */
export function normalizeBlob(raw: unknown): DashboardPrefsBlob {
  const r = (raw ?? {}) as Partial<DashboardPrefsBlob>;
  const writeScope =
    r.writeScope && typeof r.writeScope === "object" ? (r.writeScope as WriteScopeMap) : {};
  const resolveScope =
    r.resolveScope && typeof r.resolveScope === "object"
      ? (r.resolveScope as ResolveScopeMap)
      : {};
  return { writeScope, resolveScope, txFeed: isKnownTxScope(r.txFeed) ? r.txFeed : null };
}
